import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';

import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ExhibitionClaimBanner } from '@/components/ExhibitionClaimBanner';
import { ErrorState } from '@/components/ErrorState';
import { Loading } from '@/components/Loading';
import { Button } from '@/components/ui/button';
import monomoLogoWhite from '@/assets/auth/monomo-logo-white.svg';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth';
import { getHttpErrorMessage } from '@/lib/httpErrorMessage';
import { cn } from '@/lib/utils';

export interface KioskLayoutProps {
  children: React.ReactNode;
  contentClassName?: string;
}

export function KioskLayout({ children, contentClassName }: KioskLayoutProps) {
  return (
    <Auth>
      <div className="flex min-h-svh min-w-0 flex-col bg-background">
        <KioskTopBar />
        <ExhibitionClaimBanner />
        <main
          data-slot="kiosk-main-content"
          className={cn('flex min-h-0 min-w-0 flex-1 flex-col', contentClassName ?? 'gap-4 p-4')}
        >
          {children}
        </main>
      </div>
    </Auth>
  );
}

function KioskTopBar() {
  const { t } = useTranslation();
  const { logout } = useAuth({ tmp: 'KioskTopBar' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isEnding, setIsEnding] = useState(false);

  const endSession = async () => {
    if (isEnding) return;
    setIsEnding(true);
    try {
      await logout({ redirectTo: '/exhibition' });
    } finally {
      setIsEnding(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between bg-sidebar px-4">
        <img src={monomoLogoWhite} alt="Monomo" className="h-7 w-auto" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher className="w-auto max-w-[120px]" />
          <Button
            type="button"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={isEnding}
          >
            <LogOut className="mr-1.5 h-4 w-4" />
            {t('exhibition.kiosk.endSession')}
          </Button>
        </div>
      </header>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('exhibition.kiosk.endSession')}</DialogTitle>
            <DialogDescription>{t('exhibition.kiosk.endSessionConfirm')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmOpen(false)}>
              {t('generic.cancel')}
            </Button>
            <Button type="button" onClick={() => void endSession()} disabled={isEnding}>
              {t('exhibition.kiosk.endSession')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Auth({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, isLoading, error, refetchUser } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'KioskLayout',
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState>
        <h1 className="text-lg font-semibold">{t('generic.error')}</h1>
        <p className="text-sm">{getHttpErrorMessage(error, t('generic.loadError'))}</p>
        <button
          type="button"
          className="mt-2 text-sm underline"
          onClick={() => void refetchUser()}
        >
          {t('generic.retry')}
        </button>
      </ErrorState>
    );
  }

  if (!user) {
    return <Loading />;
  }

  return <>{children}</>;
}

KioskLayout.displayName = 'KioskLayout';
